import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { FullStatus } from "../api/ApiTypes";
import { formatDateTime } from "../utils/dateUtils";
import { getStatusMetadata } from "../api/OrderStatusUtil.tsx";
import { HtmlContent } from "./HtmlConent.tsx";

/**
 * StatusHistoryItem Component - Displays an individual status history item
 * Shows date in top right corner, status description, and collapsible message
 */
export const StatusHistoryItem = ({ status }: { status: FullStatus }) => {
    // State to track if content is expanded or collapsed
    const [isExpanded, setIsExpanded] = useState(true);

    // Toggle collapse state
    const toggleExpand = () => setIsExpanded(!isExpanded);

    // Only show items that aren't hidden
    if (status.hide) {
        return null;
    }

    // Get metadata for the current status
    const metadata = getStatusMetadata(status.orderStatus);

    return (
        <div className="border border-slate-200 rounded-lg bg-white mb-4 shadow-sm overflow-hidden">
            <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    {/* status info and status message */}
                    <div className="flex-1">
                        <div className="flex items-start space-x-3">
                            <div className="w-full">
                                <div className="flex items-center hover:cursor-pointer" onClick={toggleExpand}>
                                    <div
                                        className={`p-2 rounded-md flex-shrink-0 flex items-center justify-center ${metadata.bgColor}`}
                                    >
                                        {/* Apply sizing to the icon at the component level */}
                                        <span className={`flex items-center justify-center w-6 h-6 ${metadata.iconColor}`}>
                                            {metadata.icon}
                                        </span>
                                    </div>
                                    <div className="flex-1 ml-4">
                                        <h3 className="font-medium text-sm sm:text-base text-slate-800 break-all">
                                            {status.orderStatusDescription}
                                        </h3>
                                    </div>

                                    {status.orderStatusMessage && (
                                        <button
                                            className="ml-2 flex items-center text-slate-500 hover:text-slate-700 focus:outline-none hover:cursor-pointer"
                                            aria-expanded={isExpanded}
                                            aria-label={isExpanded ? "Collapse details" : "Expand details"}
                                        >
                                            {isExpanded ?
                                                <ChevronDown className="h-6 w-6" /> :
                                                <ChevronRight className="h-6 w-6" />
                                            }
                                        </button>
                                    )}
                                </div>

                                {status.orderStatusMessage && isExpanded && (
                                    <HtmlContent
                                        content={status.orderStatusMessage}
                                        className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md mt-2"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Date in top right corner */}
                    <div className="flex items-center text-slate-600 text-xs whitespace-nowrap">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>{formatDateTime(status.statusDate)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};